import React, { useState, useEffect, useMemo } from "react";
import {
  DatePicker,
  Row,
  Col,
  Card,
  Button,
  Select,
  Space,
  message,
} from "antd";
import { getUsersTypeApi } from "../../api/user";
import ListTurns from "../../components/Ambulatory/ListTurns";
import dayjs from "dayjs";
import { useSelector, useDispatch } from "react-redux";
import Spin from "../../components/Spin";
import locale from "antd/lib/date-picker/locale/es_ES";
import "dayjs/locale/es";
import { getTurnPeriodPortalApi, getHolidaysApi } from "../../api/portal";
import { onPrimaryDataRead } from "../../actions";
import {
  WhatsAppOutlined,
  DownloadOutlined,
  ShareAltOutlined,
} from "@ant-design/icons";
import "./Turns.scss";
import TurnDetail from "../../components/TurnDetail/TurnDetail";
import Footer from "../../components/Footer";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import html2canvas from "html2canvas";
import { disabledDate, generateSlots } from "../../utils/functionTurns";
import { canUseWebShare } from "../../utils/device";

dayjs.extend(utc);
dayjs.extend(timezone);

export default function Turns() {
  dayjs.locale("es");

  const {
    tenant,
    organization,
    studies,
    organizationPhone,
    tenantId,
    tenantLogo,
    tenantTimezone,
    countryCode,
  } = useSelector((store) => store.tenantData);
  const dispatch = useDispatch();
  const [users, setUsers] = useState([]);
  const [slots, setSlots] = useState();
  const [date, setDate] = useState(
    dayjs().tz(tenantTimezone).format("YYYY-MM-DDTHH:mm:ss"),
  );
  const [turn, setTurn] = useState([]);
  const [turnUser, setTurnUser] = useState(null);
  const [reload, setReload] = useState(false);
  const [size, setSize] = useState();
  const [turnType, setTurnType] = useState(null);
  const [turnDetail, setTurnDetail] = useState(false);
  const [data, setData] = useState();
  const [configSchedule, setConfigSchedule] = useState();
  const [loading, setLoading] = useState(false);
  const [allTurns, setAllTurns] = useState([]);
  const [turnDetailRef, setTurnDetailRef] = useState(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [holidays, setHolidays] = useState([]);
  const [loadingHolidays, setLoadingHolidays] = useState(true);

  // Cargar feriados desde la API
  useEffect(() => {
    const fetchHolidays = async () => {
      setLoadingHolidays(true);
      try {
        const currentYear = dayjs().year();

        // Obtener feriados para el año actual (la API devuelve año anterior, actual y siguiente)
        const response = await getHolidaysApi(
          tenantId,
          currentYear,
          countryCode,
        );

        if (response.ok) {
          setHolidays(response.holidays);
        } else {
          console.error("Error al obtener feriados:", response.message);
          message.warning("No se pudieron cargar los feriados");
          setHolidays([]);
        }
      } catch (error) {
        console.error("Error al obtener feriados:", error);
        setHolidays([]);
      } finally {
        setLoadingHolidays(false);
      }
    };

    fetchHolidays();
  }, [tenantId, countryCode]);

  useEffect(() => {
    let cancelled = false;

    const getDataTurn = async () => {
      setLoading(true);
      if (turnUser) setSlots(undefined);
      try {
        const userResponse = await getUsersTypeApi(tenantId);
        const catalog = userResponse?.users || [];
        if (cancelled) return;
        setUsers(catalog);

        if (!turnUser) return;

        const userTurn =
          turnType !== "medico"
            ? studies.find((studie) => studie.id === turnUser)
            : catalog.find((user) => user._id === turnUser);

        setSize(userTurn?.defaultSizeSlot);

        const periodTurnsResponse = await getTurnPeriodPortalApi(
          turnUser,
          tenantId,
          turnType,
        );
        if (cancelled) return;

        const periodTurns = periodTurnsResponse?.turns;
        setAllTurns(periodTurns);

        const filterDataDay = periodTurns?.find(
          (item) =>
            dayjs(date).format("DD") == item.day &&
            dayjs(date).format("MM") == item.month &&
            dayjs(date).format("YYYY") == item.year,
        );

        setTurn(filterDataDay);

        const response = await generateSlots({
          sizeSlot:
            filterDataDay?.shifts?.length > 0 || filterDataDay?.extraDay
              ? filterDataDay?.sizeSlot
              : userTurn?.defaultSizeSlot,
          turns: filterDataDay,
          userTurn,
          date,
          tenantTimezone,
          holidays,
          selectedServiceId: null,
        });
        if (cancelled) return;

        setSlots(Array.isArray(response?.filteredSlots) ? response.filteredSlots : []);
        setConfigSchedule(response?.usualConfigSchedule);
        setData(userTurn);
      } catch (error) {
        console.error("Error loading data:", error);
        if (!cancelled) setSlots([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    getDataTurn();
    setReload(false);
    return () => {
      cancelled = true;
    };
  }, [date, turnUser, reload, holidays]);

  const holidaysMap = useMemo(() => {
    const map = new Map();

    holidays.forEach((holiday) => {
      // La API devuelve { year, month, day, ... }
      // Usamos year-month-day para mayor precisión
      const key = `${holiday.year}-${holiday.month}-${holiday.day}`;
      map.set(key, true);
    });
    return map;
  }, [holidays]);

  const excludedDaysMap = useMemo(() => {
    const map = new Map();
    data?.scheduleConfig?.excludeDays
      .filter((day) => !dayjs(day).isBefore(dayjs().startOf("day"))) // Filtra solo días futuros o presentes
      .forEach((day) => {
        const formattedDay = dayjs(day)
          .tz(tenantTimezone)
          .startOf("day")
          .toISOString(); // Convertir al mismo formato de comparación
        map.set(formattedDay, true);
      });
    return map;
  }, [data?.scheduleConfig?.excludeDays]);

  if (loading && turnUser && !slots) return <Spin />;

  const formatTime = (date) => dayjs(date).tz(tenantTimezone).format("HH:mm");

  const generateImage = async () => {
    if (!turnDetailRef) return null;

    setIsGenerating(true);
    message.loading("Generando comprobante...", 0);

    try {
      // Esperar a que las imágenes se carguen
      await new Promise((resolve) => setTimeout(resolve, 1000));

      const canvas = await html2canvas(turnDetailRef, {
        scale: 2,
        backgroundColor: "#ffffff",
        useCORS: true,
        allowTaint: true,
        logging: false,
        imageTimeout: 5000,
        onclone: (document) => {
          // Asegurarse de que las imágenes estén cargadas
          const images = document.getElementsByTagName("img");
          return new Promise((resolve) => {
            const loadImages = Array.from(images).map((img) => {
              if (img.complete) return Promise.resolve();
              return new Promise((resolve) => {
                img.onload = resolve;
                img.onerror = resolve;
              });
            });
            Promise.all(loadImages).then(resolve);
          });
        },
      });

      message.destroy();
      setIsGenerating(false);
      return canvas;
    } catch (error) {
      console.error("Error al generar la imagen:", error);
      message.destroy();
      message.error("Error al generar el comprobante");
      setIsGenerating(false);
      return null;
    }
  };

  const handleVoucherAction = async () => {
    const canvas = await generateImage();
    if (!canvas) return;

    const filename = `comprobante-turno-${new Date().getTime()}.png`;
    const usesShare = canUseWebShare();

    try {
      if (usesShare) {
        const blob = await new Promise((resolve) =>
          canvas.toBlob(resolve, "image/png"),
        );
        const file = new File([blob], filename, { type: "image/png" });

        if (!navigator.canShare || navigator.canShare({ files: [file] })) {
          await navigator.share({
            files: [file],
            title: "Comprobante de Turno",
          });
          return;
        }
      }

      const link = document.createElement("a");
      link.download = filename;
      link.href = canvas.toDataURL("image/png");
      link.click();
    } catch (error) {
      if (error?.name === "AbortError") return;
      console.error("Error al obtener el comprobante:", error);
      message.error(
        usesShare
          ? "Error al compartir el comprobante"
          : "Error al descargar el comprobante",
      );
    }
  };

  const usesShare = canUseWebShare();

  return turnDetail ? (
    <div>
      <Space
        style={{
          marginTop: "20px",
          width: "100%",
          justifyContent: "center",
          display: "flex",
        }}
      >
        <Button
          type="primary"
          icon={usesShare ? <ShareAltOutlined /> : <DownloadOutlined />}
          onClick={handleVoucherAction}
          disabled={isGenerating}
          style={{
            backgroundColor: "#002a52",
            borderColor: "#002a52",
            height: 40,
            fontWeight: 600,
            borderRadius: 10,
          }}
        >
          {usesShare ? "Compartir Comprobante" : "Descargar Comprobante"}
        </Button>
      </Space>

      <div ref={setTurnDetailRef}>
        <TurnDetail
          turnDetail={turnDetail}
          doctor={
            turnType === "medico"
              ? users.find((user) => turnUser === user._id)
              : studies.find((studie) => turnUser === studie.id)
          }
          turnType={turnType}
          description={
            data?.scheduleConfig?.descriptionVisible &&
            data?.scheduleConfig?.description
          }
          cancelLink={turnDetail?.cancelLink}
          direction={turnDetail?.direction}
          phone={turnDetail?.phone}
          dni={turnDetail?.dni}
          bookedAtUnix={turnDetail?.bookedAtUnix}
        />
      </div>
    </div>
  ) : (
    <div className="online">
      <Card title={`Turnos ${organization}`}>
        <Row gutter={[20, 24]} style={{ marginBottom: 5 }}>
          <Col sm={2} />

          <Col xs={24} sm={10}>
            <Select
              showSearch
              style={{ width: "100%" }}
              placeholder="Busque por medico o especialidad"
              optionFilterProp="children"
              value={turnUser}
              onChange={(value, options) => {
                const turnType = options?.key ? "studie" : "medico";
                setTurnType(turnType);
                setTurnUser(value);
              }}
              allowClear
            >
              {users?.map((user, index) => {
                return (
                  <Select.Option value={user._id}>
                    {user.name} {user.lastname}{" "}
                    {user.medicalSpeciality && `- ${user.medicalSpeciality}`}
                  </Select.Option>
                );
              })}
              {studies?.map((studie, index) => (
                <Select.Option key={index} value={studie.id}>
                  Estudio - {studie.name}
                </Select.Option>
              ))}
            </Select>
          </Col>

          <Col xs={24} sm={10}>
            <DatePicker
              inputReadOnly
              disabled={!turnUser}
              style={{ width: "100%" }}
              format="DD-MM-YYYY"
              placeholder="Seleccione una fecha.. (*)"
              onChange={(value) => {
                setDate(
                  dayjs(value).tz(tenantTimezone).format("YYYY-MM-DDTHH:mm:ss"),
                );
              }}
              disabledDate={(current) => {
                let day = disabledDate({
                  current,
                  excludedDaysMap,
                  holidaysMap,
                  configSchedule,
                  scheduleConfig: data?.scheduleConfig,
                  allTurns,
                });
                return day?.isDisabled;
              }}
              locale={locale}
              allowClear={false}
              value={dayjs(date).tz(tenantTimezone)}
            />
          </Col>
          <Col sm={2} />
        </Row>
        {turnUser ? (
          <div>
            <ListTurns
              slots={slots}
              turnId={turn?._id}
              turns={turn?.shifts}
              turnUser={turnUser}
              setReload={setReload}
              size={size}
              turnType={turnType}
              date={date}
              setTurnDetail={setTurnDetail}
              loadingParent={loading}
              doctorData={data}
            />

            <div style={{ textAlign: "center" }}>
              <span style={{ fontSize: "8pt", color: "#888" }}>
                🗓️ Horarios habituales:
              </span>
              <div>
                {data?.scheduleConfig?.hours?.map(
                  ({ start, end, days }, idx) => (
                    <p key={idx} style={{ fontSize: "7pt", color: "#888" }}>
                      ⏰ {days && days.join(", ").toUpperCase()}:{" "}
                      {formatTime(start)} hs - {formatTime(end)} hs
                    </p>
                  ),
                )}
                <p style={{ fontSize: "6pt", color: "#888" }}>
                  Si ve turnos fuera de los horarios habituales, puede
                  solicitarlos. Se han habilitado turnos extra.
                </p>
              </div>
            </div>

            {data?.scheduleConfig?.description && (
              <div className="online-description">
                <h4>Información Importante</h4>
                <p>{data?.scheduleConfig?.description}</p>
              </div>
            )}
          </div>
        ) : (
          <>
            <div className="online-logo">
              <img
                src={`https://storage.googleapis.com/bs-proyecto1.appspot.com/${tenant}/${
                  tenantLogo || "logo"
                }.png`}
                alt="Logo"
              />
            </div>

            <div className="online-mini-logo">
              <p style={{ fontSize: "9pt" }}>
                En esta pagina solo aparecen los medicos y estudios que tienen
                su turnera web habilitada.
              </p>
              <p style={{ fontSize: "9pt" }}>
                Ante cualquier duda comunicarse con la institución o medico.
                Contacto:{" "}
                <Button type="link">
                  <a
                    href={`https://wa.me/${organizationPhone}?text=Hola%20${organization}`}
                    target="_blank"
                    referrerPolicy="origin"
                  >
                    <WhatsAppOutlined /> {organizationPhone}
                  </a>
                </Button>
              </p>
            </div>
            <div>
              <Footer />
            </div>
          </>
        )}
      </Card>
    </div>
  );
}
